import { ensureAppCheckReady, initializeFirebase } from '@/firebase/firebaseInit';
import { i18nGlobal } from '@/i18n';
import { useStoreUser } from '@/stores/storeUser';

import loggerLocal, { safeToStringLocal } from '@/utils/loggerLocal';
import { showErrorMessage, showSuccessUpdateMessage } from '@/utils/showMessage';
import { DBFunctionMeasure, DBFunctionMisc, DBFunctionOptional, DBFunctionPantone, DBFunctionPrice, DBFunctionProductModel, DBFunctionQuote, DBFunctionRegistry, DBFunctionsCompany, DBFunctionStorage, DBFunctionsUser } from '@functions/models//DBFunctions';
import type { IOGetUploadUrlRequest, IOGetUploadUrlResponse } from '@functions/models/ioInterfaces/ioGetUploadUrl';
import type { IOGetUserRequest, IOGetUserResponse } from '@functions/models/ioInterfaces/ioGetUser';
import type { IOSignInRequest, IOSignInResponse } from '@functions/models/ioInterfaces/ioSignIn';
import type { IOSignUpRequest, IOSignUpResponse } from '@functions/models/ioInterfaces/ioSignUp';

import { httpsCallable } from 'firebase/functions';

import { isEmptyCheckLocal } from '@/utils/utils';
import { DBRetCode } from '@functions/models/DBEnum';
import type { IOCloneQuoteRequest, IOCloneQuoteResponse } from '@functions/models/ioInterfaces/ioCloneQuote';
import type { IOGetProductModelMeasurePriceRequest, IOGetProductModelMeasurePriceResponse } from '@functions/models/ioInterfaces/ioGetProductModelMeasurePrice';
import type { IOGetQuoteDataRequest, IOGetQuoteDataResponse } from '@functions/models/ioInterfaces/ioGetQuoteData';
import type { IOListAccessoryRequest, IOListAccessoryResponse } from '@functions/models/ioInterfaces/ioListAccessory';
import type { IOListAttachmentRequest, IOListAttachmentResponse } from '@functions/models/ioInterfaces/ioListAttachment';
import type { IOListCodeTablesRequest, IOListCodeTablesResponse } from '@functions/models/ioInterfaces/ioListCodeTables';
import type { IOListCodeTablesNameRequest, IOListCodeTablesNameResponse } from '@functions/models/ioInterfaces/ioListCodeTablesName';
import type { IOListColorRequest, IOListColorResponse } from '@functions/models/ioInterfaces/ioListColor';
import type { IOListCompanyRequest, IOListCompanyResponse } from '@functions/models/ioInterfaces/ioListCompany';
import type { IOListCompanyLinkRequest, IOListCompanyLinkResponse } from '@functions/models/ioInterfaces/ioListCompanyLink';
import type { IOListCompanyUserRequest, IOListCompanyUserResponse } from '@functions/models/ioInterfaces/ioListCompanyUser';
import type { IOListMaterialTypeColorRequest, IOListMaterialTypeColorResponse } from '@functions/models/ioInterfaces/ioListMaterialTypeColor';
import type { IOListMeasureRequest, IOListMeasureResponse } from '@functions/models/ioInterfaces/ioListMeasure';
import type { IOListMeasureGroupRequest, IOListMeasureGroupResponse } from '@functions/models/ioInterfaces/ioListMeasureGroup';
import type { IOListOptionalRequest, IOListOptionalResponse } from '@functions/models/ioInterfaces/ioListOptional';
import type { IOListOptionalDetailRequest, IOListOptionalDetailResponse } from '@functions/models/ioInterfaces/ioListOptionalDetail';
import type { IOListOptionalDetailWithLinkRequest, IOListOptionalDetailWithLinkResponse } from '@functions/models/ioInterfaces/ioListOptionalDetailWithLink';
import type { IOListPantoneRequest, IOListPantoneResponse } from '@functions/models/ioInterfaces/ioListPantone';
import type { IOListPantoneColorRequest, IOListPantoneColorResponse } from '@functions/models/ioInterfaces/ioListPantoneColor';
import type { IOListPantoneProductModelRequest, IOListPantoneProductModelResponse } from '@functions/models/ioInterfaces/ioListPantoneProductModel';
import type { IOListPriceGroupMeasureRequest, IOListPriceGroupMeasureResponse } from '@functions/models/ioInterfaces/ioListPriceGroupMeasure';
import type { IOListPriceMeasureRequest, IOListPriceMeasureResponse } from '@functions/models/ioInterfaces/ioListPriceMeasure';
import type { IOListProductModelRequest, IOListProductModelResponse } from '@functions/models/ioInterfaces/ioListProductModel';
import type { IOListProductModelColorRequest, IOListProductModelColorResponse } from "@functions/models/ioInterfaces/ioListProductModelColor";
import type { IOListProductModelMeasureGroupRequest, IOListProductModelMeasureGroupResponse } from '@functions/models/ioInterfaces/ioListProductModelMeasureGroup';
import type { IOListProductModelOptionalRequest, IOListProductModelOptionalResponse } from '@functions/models/ioInterfaces/ioListProductModelOptional';
import type { IOListProductModelOptionalDetailRequest, IOListProductModelOptionalDetailResponse } from '@functions/models/ioInterfaces/ioListProductModelOptionalDetail';
import type { IOListProductModelOptionalPriceGroupMeasureRequest, IOListProductModelOptionalPriceGroupMeasureResponse } from '@functions/models/ioInterfaces/ioListProductModelOptionalPriceGroupMeasure';
import type { IOListQuoteRequest, IOListQuoteResponse } from '@functions/models/ioInterfaces/ioListQuote';
import type { IOListQuoteAccessoryRequest, IOListQuoteAccessoryResponse } from '@functions/models/ioInterfaces/ioListQuoteAccessory';
import type { IOListQuoteDetailRequest, IOListQuoteDetailResponse } from '@functions/models/ioInterfaces/ioListQuoteDetail';
import type { IOListQuoteDetailOptionalRequest, IOListQuoteDetailOptionalResponse } from '@functions/models/ioInterfaces/ioListQuoteDetailOptional';
import type { IOListQuoteOptionalRequest, IOListQuoteOptionalResponse } from '@functions/models/ioInterfaces/ioListQuoteOptional';
import type { IOListQuoteVersionRequest, IOListQuoteVersionResponse } from '@functions/models/ioInterfaces/ioListQuoteVersion';
import type { IOListRegistryRequest, IOListRegistryResponse } from '@functions/models/ioInterfaces/ioListRegistry';
import type { IOMutateAccessoryRequest, IOMutateAccessoryResponse } from '@functions/models/ioInterfaces/ioMutateAccessory';
import type { IOMutateAttachmentRequest, IOMutateAttachmentResponse } from '@functions/models/ioInterfaces/ioMutateAttachment';
import type { IOMutateCodeTablesRequest, IOMutateCodeTablesResponse } from '@functions/models/ioInterfaces/ioMutateCodeTables';
import type { IOMutateCodeTablesNameRequest, IOMutateCodeTablesNameResponse } from '@functions/models/ioInterfaces/ioMutateCodeTablesName';
import type { IOMutateColorRequest, IOMutateColorResponse } from '@functions/models/ioInterfaces/ioMutateColor';
import type { IOMutateCompanyRequest, IOMutateCompanyResponse } from '@functions/models/ioInterfaces/ioMutateCompany';
import type { IOMutateCompanyLinkRequest, IOMutateCompanyLinkResponse } from '@functions/models/ioInterfaces/ioMutateCompanyLink';
import type { IOMutateMeasureRequest, IOMutateMeasureResponse } from '@functions/models/ioInterfaces/ioMutateMeasure';
import type { IOMutateMeasureGroupRequest, IOMutateMeasureGroupResponse } from '@functions/models/ioInterfaces/ioMutateMeasureGroup';
import type { IOMutateOptionalRequest, IOMutateOptionalResponse } from '@functions/models/ioInterfaces/ioMutateOptional';
import type { IOMutateOptionalDetailRequest, IOMutateOptionalDetailResponse } from '@functions/models/ioInterfaces/ioMutateOptionalDetail';
import type { IOMutateOptionalDetailLinkRequest, IOMutateOptionalDetailLinkResponse } from '@functions/models/ioInterfaces/ioMutateOptionalDetailLink';
import type { IOMutatePantoneRequest, IOMutatePantoneResponse } from '@functions/models/ioInterfaces/ioMutatePantone';
import type { IOMutatePantoneColorRequest, IOMutatePantoneColorResponse } from '@functions/models/ioInterfaces/ioMutatePantoneColor';
import type { IOMutatePantoneProductModelRequest, IOMutatePantoneProductModelResponse } from '@functions/models/ioInterfaces/ioMutatePantoneProductModel';
import type { IOMutatePriceGroupMeasureRequest, IOMutatePriceGroupMeasureResponse } from '@functions/models/ioInterfaces/ioMutatePriceGroupMeasure';
import type { IOMutatePriceMeasureRequest, IOMutatePriceMeasureResponse } from '@functions/models/ioInterfaces/ioMutatePriceMeasure';
import type { IOMutateProductModelRequest, IOMutateProductModelResponse } from '@functions/models/ioInterfaces/ioMutateProductModel';
import type { IOMutateProductModelMeasureGroupRequest, IOMutateProductModelMeasureGroupResponse } from '@functions/models/ioInterfaces/ioMutateProductModelMeasureGroup';
import type { IOMutateProductModelOptionalRequest, IOMutateProductModelOptionalResponse } from '@functions/models/ioInterfaces/ioMutateProductModelOptional';
import type { IOMutateProductModelOptionalPriceGroupMeasureRequest, IOMutateProductModelOptionalPriceGroupMeasureResponse } from '@functions/models/ioInterfaces/ioMutateProductModelOptionalPriceGroupMeasure';
import type { IOMutateQuoteRequest, IOMutateQuoteResponse } from '@functions/models/ioInterfaces/ioMutateQuote';
import type { IOMutateQuoteAccessoryRequest, IOMutateQuoteAccessoryResponse } from '@functions/models/ioInterfaces/ioMutateQuoteAccessory';
import type { IOMutateQuoteDetailRequest, IOMutateQuoteDetailResponse } from '@functions/models/ioInterfaces/ioMutateQuoteDetail';
import type { IOMutateQuoteDetailOptionalRequest, IOMutateQuoteDetailOptionalResponse } from '@functions/models/ioInterfaces/ioMutateQuoteDetailOptional';
import type { IOMutateQuoteOptionalRequest, IOMutateQuoteOptionalResponse } from '@functions/models/ioInterfaces/ioMutateQuoteOptional';
import type { IOMutateQuoteStatusRequest, IOMutateQuoteStatusResponse } from '@functions/models/ioInterfaces/ioMutateQuoteStatus';
import type { IOMutateQuoteVersionRequest, IOMutateQuoteVersionResponse } from '@functions/models/ioInterfaces/ioMutateQuoteVersion';
import type { IOMutateRegistryRequest, IOMutateRegistryResponse } from '@functions/models/ioInterfaces/ioMutateRegistry';
import type { IOMutateUserRequest, IOMutateUserResponse } from '@functions/models/ioInterfaces/ioMutateUser';
import type { IOMutateUserCompanyRequest, IOMutateUserCompanyResponse } from '@functions/models/ioInterfaces/ioMutateUserCompany';
import type { IOQuoteCalculatePriceRequest, IOQuoteCalculatePriceResponse } from '@functions/models/ioInterfaces/ioQuoteCalculatePrice';
import type { IOSetAuthRequest, IOSetAuthResponse } from '@functions/models/ioInterfaces/ioSetAuth';

const { functionsFire } = await initializeFirebase()

export type FunctionName =
  | ApiUserFunctionName
  | ApiCompanyFunctionName
  | ApiStorageFunctionName
  | ApiProductModelFunctionName
  | ApiPantoneFunctionName
  | ApiOptionalFunctionName
  | ApiMeasureFunctionName
  | ApiMiscFunctionName
  | ApiRegistryFunctionName
  | ApiQuoteFunctionName
  | ApiPriceFunctionName

export async function backEndCall ( functionName: FunctionName, data: any, throwError = true ): Promise<any> {
  await ensureAppCheckReady()
  const addMessage = httpsCallable( functionsFire, functionName )
  const storeUser = useStoreUser()
  const idCompany = storeUser && storeUser.companyData ? storeUser?.companyData?.id ?? '' : 'NoCompany'
  const idUser = storeUser && storeUser.userData ? storeUser?.userData?.id ?? '' : 'NoUser'
  const newData = {
    ...data,
    xxArchIdCompany: idCompany,
    xxArchIdUser: idUser,
  }
  const res = await addMessage( newData )
    .then( result => {

      return result.data
    } )
    .catch( error => {

      loggerLocal.error( 'BackEndCall Error: ' + functionName, error )

      if ( throwError )
      {
        loggerLocal.error( 'Error: ' + safeToStringLocal( error ) )
        const name = error?.name ?? ''
        const code = error?.code ?? ''


        loggerLocal.error( 'Error: ->' + code + '<->' + name + '<-' )
        if ( code == 'functions/permission-denied' )
        {

          showErrorMessage( i18nGlobal.t( 'error.notAuthorized' ), name.toString() )
          return undefined
        } else
        {
          throw error
        }
      } else
      {
        return undefined
      }
    } )

  return res
}

export async function backEndCallExtended ( functionName: FunctionName, data: any, isMutation = false, throwError = true, hideSuccessMsg = false ): Promise<any> {
  const functionNameLog = backEndCallExtended.name + ' - ' + functionName
  const errorType2Show = isMutation ? i18nGlobal.t( 'error.mutationFailed' ) : i18nGlobal.t( 'error.listFailed' )
  const errorResult = i18nGlobal.t( 'error.result' ) + ' ' + functionNameLog + ': '
  try
  {
    const res = await backEndCall( functionName, data, throwError )
    if ( !isEmptyCheckLocal( res ) && !isEmptyCheckLocal( res.result ) )
    {
      const result = res.result
      if ( throwError )
      {
        if ( isMutation && result !== DBRetCode.SUCCESS )
        {
          showErrorMessage( errorType2Show, errorResult + safeToStringLocal( result ) )
        } else if ( !isMutation && result == DBRetCode.ERROR )
        {
          showErrorMessage( errorType2Show, errorResult + safeToStringLocal( result ) )
        }
      }
    } else
    {
      const result = res.result
      if ( isMutation && result == DBRetCode.SUCCESS && !hideSuccessMsg )
      {
        //loggerLocal.info( functionNameLog + ' Mutation success', result, isMutation, hideSuccessMsg )
        showSuccessUpdateMessage()
      }
    }
    return res
  } catch ( error )
  {
    loggerLocal.error( functionNameLog + ' Error: ', error )
    if ( throwError )
    {

      const name = ( error as any )?.name ?? ''
      const code = ( error as any )?.code ?? ''

      if ( code == 'functions/permission-denied' )
      {
        //showErrorMessage( i18nGlobal.t( 'error.notAuthorized' ), name.toString() )
        return undefined
      } else
      {
        showErrorMessage( errorType2Show, errorResult + safeToStringLocal( error ) )
      }
    } else
    {
      return undefined
    }
  }
}
const defaultApiPrefix = 'apiGenericV2/'
const apiUserPrefix = defaultApiPrefix // 'apiUserV2/'
const apiCompanyPrefix = defaultApiPrefix // 'apiCompanyV2/'
const apiStoragePrefix = defaultApiPrefix // 'apiStorageV2/'
const apiProductModelPrefix = defaultApiPrefix // 'apiProductModelV2/'
const apiPantonePrefix = defaultApiPrefix // 'apiPantoneV2/'
const apiOptionalPrefix = defaultApiPrefix // 'apiOptionalV2/'
const apiMeasurePrefix = defaultApiPrefix // 'apiMeasureV2/'
const apiMiscPrefix = defaultApiPrefix // 'apiMiscV2/'
const apiRegistryPrefix = defaultApiPrefix // 'apiRegistryV2/'
const apiQuotePrefix = defaultApiPrefix // 'apiQuoteV2/'
const apiPricePrefix = defaultApiPrefix // 'apiPriceV2/'

export enum ApiUserFunctionName {
  signUpUser = apiUserPrefix + DBFunctionsUser.signup,
  signinUser = apiUserPrefix + DBFunctionsUser.signin,
  users = apiUserPrefix + DBFunctionsUser.users,
  mutateUser = apiUserPrefix + DBFunctionsUser.mutateUser,
  listCompanyUser = apiUserPrefix + DBFunctionsUser.listCompanyUser,
  mutateUserCompany = apiUserPrefix + DBFunctionsUser.mutateUserCompany,
  getUser = apiUserPrefix + DBFunctionsUser.getUser,
  setAuth = apiUserPrefix + DBFunctionsUser.setAuth,
}
export enum ApiCompanyFunctionName {
  mutateCompany = apiCompanyPrefix + DBFunctionsCompany.mutateCompany,
  listCompany = apiCompanyPrefix + DBFunctionsCompany.listCompany,
  listCompanyLink = apiCompanyPrefix + DBFunctionsCompany.listCompanyLink,
  mutateCompanyLink = apiCompanyPrefix + DBFunctionsCompany.mutateCompanyLink,
}

export enum ApiStorageFunctionName {
  getUploadUrl = apiStoragePrefix + DBFunctionStorage.getUploadUrl,
  getDownloadUrl = apiStoragePrefix + DBFunctionStorage.getDownloadUrl,
  listAttachment = apiStoragePrefix + DBFunctionStorage.listAttachment,
  mutateAttachment = apiStoragePrefix + DBFunctionStorage.mutateAttachment,
}

export enum ApiProductModelFunctionName {
  mutateProductModel = apiProductModelPrefix + DBFunctionProductModel.mutateProductModel,
  listProductModel = apiProductModelPrefix + DBFunctionProductModel.listProductModel,
  listProductModelOptional = apiProductModelPrefix + DBFunctionProductModel.listProductModelOptional,
  mutateProductModelOptional = apiProductModelPrefix + DBFunctionProductModel.mutateProductModelOptional,
  listProductModelMeasureGroup = apiProductModelPrefix + DBFunctionProductModel.listProductModelMeasureGroup,
  mutateProductModelMeasureGroup = apiProductModelPrefix + DBFunctionProductModel.mutateProductModelMeasureGroup,
  listProductModelColor = apiProductModelPrefix + DBFunctionProductModel.listProductModelColor,
  listProductModelOptionalDetail = apiProductModelPrefix + DBFunctionProductModel.listProductModelOptionalDetail,

}

export enum ApiPantoneFunctionName {
  listPantone = apiPantonePrefix + DBFunctionPantone.listPantone,
  mutatePantone = apiPantonePrefix + DBFunctionPantone.mutatePantone,
  listColor = apiPantonePrefix + DBFunctionPantone.listColor,
  mutateColor = apiPantonePrefix + DBFunctionPantone.mutateColor,
  listPantoneColor = apiPantonePrefix + DBFunctionPantone.listPantoneColor,
  mutatePantoneColor = apiPantonePrefix + DBFunctionPantone.mutatePantoneColor,
  mutatePantoneProductModel = apiPantonePrefix + DBFunctionPantone.mutatePantoneProductModel,
  listPantoneProductModel = apiPantonePrefix + DBFunctionPantone.listPantoneProductModel,
  listMaterialTypeColor = apiPantonePrefix + DBFunctionPantone.listMaterialTypeColor,
}

export enum ApiOptionalFunctionName {
  mutateOptional = apiOptionalPrefix + DBFunctionOptional.mutateOptional,
  listOptional = apiOptionalPrefix + DBFunctionOptional.listOptional,
  mutateOptionalDetail = apiOptionalPrefix + DBFunctionOptional.mutateOptionalDetail,
  listOptionalDetail = apiOptionalPrefix + DBFunctionOptional.listOptionalDetail,
  listOptionalDetailWithLink = apiOptionalPrefix + DBFunctionOptional.listOptionalDetailWithLink,
  mutateOptionalDetailLink = apiOptionalPrefix + DBFunctionOptional.mutateOptionalDetailLink,
  mutateAccessory = apiOptionalPrefix + DBFunctionOptional.mutateAccessory,
  listAccessory = apiOptionalPrefix + DBFunctionOptional.listAccessory,
}

export enum ApiMeasureFunctionName {
  mutateMeasure = apiMeasurePrefix + DBFunctionMeasure.mutateMeasure,
  listMeasure = apiMeasurePrefix + DBFunctionMeasure.listMeasure,
  listMeasureGroup = apiMeasurePrefix + DBFunctionMeasure.listMeasureGroup,
  mutateMeasureGroup = apiMeasurePrefix + DBFunctionMeasure.mutateMeasureGroup,
}

export enum ApiMiscFunctionName {
  mutateCodeTablesName = apiMiscPrefix + DBFunctionMisc.mutateCodeTablesName,
  listCodeTablesName = apiMiscPrefix + DBFunctionMisc.listCodeTablesName,
  mutateCodeTables = apiMiscPrefix + DBFunctionMisc.mutateCodeTables,
  listCodeTables = apiMiscPrefix + DBFunctionMisc.listCodeTables,
}

export enum ApiRegistryFunctionName {
  mutateRegistry = apiRegistryPrefix + DBFunctionRegistry.mutateRegistry,
  listRegistry = apiRegistryPrefix + DBFunctionRegistry.listRegistry,
}

export enum ApiQuoteFunctionName {
  mutateQuote = apiQuotePrefix + DBFunctionQuote.mutateQuote,
  listQuote = apiQuotePrefix + DBFunctionQuote.listQuote,
  mutateQuoteVersion = apiQuotePrefix + DBFunctionQuote.mutateQuoteVersion,
  listQuoteVersion = apiQuotePrefix + DBFunctionQuote.listQuoteVersion,
  mutateQuoteDetail = apiQuotePrefix + DBFunctionQuote.mutateQuoteDetail,
  listQuoteDetail = apiQuotePrefix + DBFunctionQuote.listQuoteDetail,
  mutateQuoteOptional = apiQuotePrefix + DBFunctionQuote.mutateQuoteOptional,
  listQuoteOptional = apiQuotePrefix + DBFunctionQuote.listQuoteOptional,
  getQuoteData = apiQuotePrefix + DBFunctionQuote.getQuoteData,
  mutateQuoteDetailOptional = apiQuotePrefix + DBFunctionQuote.mutateQuoteDetailOptional,
  listQuoteDetailOptional = apiQuotePrefix + DBFunctionQuote.listQuoteDetailOptional,
  mutateQuoteAccessory = apiQuotePrefix + DBFunctionQuote.mutateQuoteAccessory,
  listQuoteAccessory = apiQuotePrefix + DBFunctionQuote.listQuoteAccessory,
  quoteCalculatePrice = apiQuotePrefix + DBFunctionQuote.quoteCalculatePrice,
  mutateQuoteStatus = apiQuotePrefix + DBFunctionQuote.mutateQuoteStatus,
  cloneQuote = apiQuotePrefix + DBFunctionQuote.cloneQuote,
}

export enum ApiPriceFunctionName {
  mutatePriceGroupMeasure = apiPricePrefix + DBFunctionPrice.mutatePriceGroupMeasure,
  listPriceGroupMeasure = apiPricePrefix + DBFunctionPrice.listPriceGroupMeasure,
  mutatePriceMeasure = apiPricePrefix + DBFunctionPrice.mutatePriceMeasure,
  listPriceMeasure = apiPricePrefix + DBFunctionPrice.listPriceMeasure,
  mutateProductModelOptionalPriceGroupMeasure = apiPricePrefix + DBFunctionPrice.mutateProductModelOptionalPriceGroupMeasure,
  listProductModelOptionalPriceGroupMeasure = apiPricePrefix + DBFunctionPrice.listProductModelOptionalPriceGroupMeasure,
  getProductModelMeasurePrice = apiPricePrefix + DBFunctionPrice.getProductModelMeasurePrice,
}

export async function be_signUpUser ( data: IOSignUpRequest ): Promise<IOSignUpResponse> {
  return await backEndCall( ApiUserFunctionName.signUpUser, data )
}

export async function be_getLoginUserData ( data: IOSignInRequest ): Promise<IOSignInResponse> {
  const getLoginUserDatares = await backEndCall( ApiUserFunctionName.signinUser, data )
  return getLoginUserDatares
}

export async function be_getUser ( data: IOGetUserRequest ): Promise<IOGetUserResponse> {
  const getLoginUserDatares = await backEndCall( ApiUserFunctionName.getUser, data )
  return getLoginUserDatares
}

export async function be_listCompanyUser ( data: IOListCompanyUserRequest ): Promise<IOListCompanyUserResponse> {
  return await backEndCall( ApiUserFunctionName.listCompanyUser, data )
}

export async function be_mutateUser ( data: IOMutateUserRequest ): Promise<IOMutateUserResponse> {
  return await backEndCall( ApiUserFunctionName.mutateUser, data )
}

export async function be_mutateUserCompany ( data: IOMutateUserCompanyRequest ): Promise<IOMutateUserCompanyResponse> {
  return await backEndCall( ApiUserFunctionName.mutateUserCompany, data )
}

export async function be_setAuth ( data: IOSetAuthRequest ): Promise<IOSetAuthResponse> {
  return await backEndCall( ApiUserFunctionName.setAuth, data )
}

//SECTION - company call

export async function be_mutateCompany ( data: IOMutateCompanyRequest ): Promise<IOMutateCompanyResponse> {
  return await backEndCall( ApiCompanyFunctionName.mutateCompany, data )
  //return await backEndCall(ApiUserFunctionName.mutateCompany, data)
}
export async function be_listCompany ( data: IOListCompanyRequest ): Promise<IOListCompanyResponse> {
  return await backEndCall( ApiCompanyFunctionName.listCompany, data )
}
export async function be_listCompanyLink ( data: IOListCompanyLinkRequest ): Promise<IOListCompanyLinkResponse> {
  return await backEndCallExtended( ApiCompanyFunctionName.listCompanyLink, data, false, true )
}
export async function be_mutateCompanyLink ( data: IOMutateCompanyLinkRequest ): Promise<IOMutateCompanyLinkResponse> {
  return await backEndCallExtended( ApiCompanyFunctionName.mutateCompanyLink, data, true, true )
}


//SECTION - storage call
export async function be_getUploadUrl ( data: IOGetUploadUrlRequest ): Promise<IOGetUploadUrlResponse> {
  return await backEndCall( ApiStorageFunctionName.getUploadUrl, data )
}

export async function be_getDownloadUrl ( data: IOGetUploadUrlRequest ): Promise<IOGetUploadUrlResponse> {
  return await backEndCall( ApiStorageFunctionName.getDownloadUrl, data )
}

export async function be_listAttachment ( data: IOListAttachmentRequest ): Promise<IOListAttachmentResponse> {
  return await backEndCall( ApiStorageFunctionName.listAttachment, data )
}

export async function be_mutateAttachment ( data: IOMutateAttachmentRequest ): Promise<IOMutateAttachmentResponse> {
  return await backEndCall( ApiStorageFunctionName.mutateAttachment, data )
}

//!SECTION - Product Model call
export async function be_mutateProductModel ( data: IOMutateProductModelRequest ): Promise<IOMutateProductModelResponse> {
  return await backEndCallExtended( ApiProductModelFunctionName.mutateProductModel, data, true, true )
}
export async function be_listProductModel ( data: IOListProductModelRequest ): Promise<IOListProductModelResponse> {
  return await backEndCall( ApiProductModelFunctionName.listProductModel, data )
}
export async function be_listProductModelOptional ( data: IOListProductModelOptionalRequest ): Promise<IOListProductModelOptionalResponse> {
  return await backEndCall( ApiProductModelFunctionName.listProductModelOptional, data )
}
export async function be_mutateProductModelOptional ( data: IOMutateProductModelOptionalRequest ): Promise<IOMutateProductModelOptionalResponse> {
  return await backEndCall( ApiProductModelFunctionName.mutateProductModelOptional, data )
}
export async function be_listProductModelMeasureGroup ( data: IOListProductModelMeasureGroupRequest ): Promise<IOListProductModelMeasureGroupResponse> {
  return await backEndCall( ApiProductModelFunctionName.listProductModelMeasureGroup, data )
}
export async function be_mutateProductModelMeasureGroup ( data: IOMutateProductModelMeasureGroupRequest ): Promise<IOMutateProductModelMeasureGroupResponse> {
  return await backEndCall( ApiProductModelFunctionName.mutateProductModelMeasureGroup, data )
}
export async function be_listProductModelColor ( data: IOListProductModelColorRequest ): Promise<IOListProductModelColorResponse> {
  return await backEndCall( ApiProductModelFunctionName.listProductModelColor, data )
}
export async function be_listProductModelOptionalDetail ( data: IOListProductModelOptionalDetailRequest ): Promise<IOListProductModelOptionalDetailResponse> {
  return await backEndCall( ApiProductModelFunctionName.listProductModelOptionalDetail, data )
}
//!SECTION - Pantone call
export async function be_listPantone ( data: IOListPantoneRequest ): Promise<IOListPantoneResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.listPantone, data, false, true )
}
export async function be_mutatePantone ( data: IOMutatePantoneRequest ): Promise<IOMutatePantoneResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.mutatePantone, data, true, true )
}
export async function be_listColor ( data: IOListColorRequest ): Promise<IOListColorResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.listColor, data, false, true )
}
export async function be_mutateColor ( data: IOMutateColorRequest ): Promise<IOMutateColorResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.mutateColor, data, true, true )
}
export async function be_listPantoneColor ( data: IOListPantoneColorRequest ): Promise<IOListPantoneColorResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.listPantoneColor, data, false, true )
}
export async function be_mutatePantoneColor ( data: IOMutatePantoneColorRequest ): Promise<IOMutatePantoneColorResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.mutatePantoneColor, data, true, true )
}
export async function be_mutatePantoneProductModel ( data: IOMutatePantoneProductModelRequest ): Promise<IOMutatePantoneProductModelResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.mutatePantoneProductModel, data, true, true )
}
export async function be_listPantoneProductModel ( data: IOListPantoneProductModelRequest ): Promise<IOListPantoneProductModelResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.listPantoneProductModel, data, false, true )
}
export async function be_listMaterialTypeColor ( data: IOListMaterialTypeColorRequest ): Promise<IOListMaterialTypeColorResponse> {
  return await backEndCallExtended( ApiPantoneFunctionName.listMaterialTypeColor, data, false, true )
}

//!SECTION - Optional call
export async function be_mutateOptional ( data: IOMutateOptionalRequest ): Promise<IOMutateOptionalResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.mutateOptional, data, true, true )
}
export async function be_listOptional ( data: IOListOptionalRequest ): Promise<IOListOptionalResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.listOptional, data, false, true )
}
export async function be_mutateOptionalDetail ( data: IOMutateOptionalDetailRequest ): Promise<IOMutateOptionalDetailResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.mutateOptionalDetail, data, true, true )
}
export async function be_listOptionalDetail ( data: IOListOptionalDetailRequest ): Promise<IOListOptionalDetailResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.listOptionalDetail, data, false, true )
}
export async function be_listOptionalDetailWithLink ( data: IOListOptionalDetailWithLinkRequest ): Promise<IOListOptionalDetailWithLinkResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.listOptionalDetailWithLink, data, false, true )
}
export async function be_mutateOptionalDetailLink ( data: IOMutateOptionalDetailLinkRequest ): Promise<IOMutateOptionalDetailLinkResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.mutateOptionalDetailLink, data, true, true )
}
export async function be_mutateAccessory ( data: IOMutateAccessoryRequest ): Promise<IOMutateAccessoryResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.mutateAccessory, data, true, true )
}
export async function be_listAccessory ( data: IOListAccessoryRequest ): Promise<IOListAccessoryResponse> {
  return await backEndCallExtended( ApiOptionalFunctionName.listAccessory, data, false, true )
}

//!SECTION - Measure call
export async function be_mutateMeasure ( data: IOMutateMeasureRequest ): Promise<IOMutateMeasureResponse> {
  return await backEndCallExtended( ApiMeasureFunctionName.mutateMeasure, data, true, true )
}
export async function be_listMeasure ( data: IOListMeasureRequest ): Promise<IOListMeasureResponse> {
  return await backEndCallExtended( ApiMeasureFunctionName.listMeasure, data, false, true )
}
export async function be_listMeasureGroup ( data: IOListMeasureGroupRequest ): Promise<IOListMeasureGroupResponse> {
  return await backEndCallExtended( ApiMeasureFunctionName.listMeasureGroup, data, false, true )
}
export async function be_mutateMeasureGroup ( data: IOMutateMeasureGroupRequest ): Promise<IOMutateMeasureGroupResponse> {
  return await backEndCallExtended( ApiMeasureFunctionName.mutateMeasureGroup, data, true, true )
}

//!SECTION - Misc call
export async function be_mutateCodeTablesName ( data: IOMutateCodeTablesNameRequest ): Promise<IOMutateCodeTablesNameResponse> {
  return await backEndCallExtended( ApiMiscFunctionName.mutateCodeTablesName, data, true, true )
}
export async function be_listCodeTablesName ( data: IOListCodeTablesNameRequest ): Promise<IOListCodeTablesNameResponse> {
  return await backEndCallExtended( ApiMiscFunctionName.listCodeTablesName, data, false, true )
}
export async function be_mutateCodeTables ( data: IOMutateCodeTablesRequest ): Promise<IOMutateCodeTablesResponse> {
  return await backEndCallExtended( ApiMiscFunctionName.mutateCodeTables, data, true, true )
}
export async function be_listCodeTables ( data: IOListCodeTablesRequest ): Promise<IOListCodeTablesResponse> {
  return await backEndCallExtended( ApiMiscFunctionName.listCodeTables, data, false, true )
}

//!SECTION - Registry call
export async function be_mutateRegistry ( data: IOMutateRegistryRequest ): Promise<IOMutateRegistryResponse> {
  return await backEndCallExtended( ApiRegistryFunctionName.mutateRegistry, data, true, true )
}
export async function be_listRegistry ( data: IOListRegistryRequest ): Promise<IOListRegistryResponse> {
  return await backEndCallExtended( ApiRegistryFunctionName.listRegistry, data, false, true )
}


//!SECTION - Quote call
export async function be_mutateQuote ( data: IOMutateQuoteRequest ): Promise<IOMutateQuoteResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuote, data, true, true, true )
}
export async function be_listQuote ( data: IOListQuoteRequest ): Promise<IOListQuoteResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuote, data, false, true )
}
export async function be_mutateQuoteVersion ( data: IOMutateQuoteVersionRequest ): Promise<IOMutateQuoteVersionResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteVersion, data, true, true )
}
export async function be_listQuoteVersion ( data: IOListQuoteVersionRequest ): Promise<IOListQuoteVersionResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuoteVersion, data, false, true )
}
export async function be_mutateQuoteDetail ( data: IOMutateQuoteDetailRequest ): Promise<IOMutateQuoteDetailResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteDetail, data, true, true )
}
export async function be_listQuoteDetail ( data: IOListQuoteDetailRequest ): Promise<IOListQuoteDetailResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuoteDetail, data, false, true )
}
export async function be_mutateQuoteOptional ( data: IOMutateQuoteOptionalRequest ): Promise<IOMutateQuoteOptionalResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteOptional, data, true, true )
}
export async function be_listQuoteOptional ( data: IOListQuoteOptionalRequest ): Promise<IOListQuoteOptionalResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuoteOptional, data, false, true )
}
export async function be_getQuoteData ( data: IOGetQuoteDataRequest ): Promise<IOGetQuoteDataResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.getQuoteData, data, false, true )
}
export async function be_mutateQuoteDetailOptional ( data: IOMutateQuoteDetailOptionalRequest ): Promise<IOMutateQuoteDetailOptionalResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteDetailOptional, data, true, true )
}
export async function be_listQuoteDetailOptional ( data: IOListQuoteDetailOptionalRequest ): Promise<IOListQuoteDetailOptionalResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuoteDetailOptional, data, false, true )
}
export async function be_mutateQuoteAccessory ( data: IOMutateQuoteAccessoryRequest ): Promise<IOMutateQuoteAccessoryResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteAccessory, data, true, true )
}
export async function be_listQuoteAccessory ( data: IOListQuoteAccessoryRequest ): Promise<IOListQuoteAccessoryResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.listQuoteAccessory, data, false, true )
}
export async function be_quoteCalculatePrice ( data: IOQuoteCalculatePriceRequest ): Promise<IOQuoteCalculatePriceResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.quoteCalculatePrice, data, true, true )
}
export async function be_mutateQuoteStatus ( data: IOMutateQuoteStatusRequest ): Promise<IOMutateQuoteStatusResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.mutateQuoteStatus, data, true, true, false )
}
export async function be_cloneQuote ( data: IOCloneQuoteRequest ): Promise<IOCloneQuoteResponse> {
  return await backEndCallExtended( ApiQuoteFunctionName.cloneQuote, data, true, true, false )
}


//!SECTION - Price call
export async function be_mutatePriceGroupMeasure ( data: IOMutatePriceGroupMeasureRequest ): Promise<IOMutatePriceGroupMeasureResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.mutatePriceGroupMeasure, data, true, true )
}
export async function be_listPriceGroupMeasure ( data: IOListPriceGroupMeasureRequest ): Promise<IOListPriceGroupMeasureResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.listPriceGroupMeasure, data, false, true )
}
export async function be_mutatePriceMeasure ( data: IOMutatePriceMeasureRequest ): Promise<IOMutatePriceMeasureResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.mutatePriceMeasure, data, true, true )
}
export async function be_listPriceMeasure ( data: IOListPriceMeasureRequest ): Promise<IOListPriceMeasureResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.listPriceMeasure, data, false, true )
}
export async function be_mutateProductModelOptionalPriceGroupMeasure ( data: IOMutateProductModelOptionalPriceGroupMeasureRequest ): Promise<IOMutateProductModelOptionalPriceGroupMeasureResponse> {
  //need to manage warning message
  return await backEndCall( ApiPriceFunctionName.mutateProductModelOptionalPriceGroupMeasure, data, true )
}
export async function be_listProductModelOptionalPriceGroupMeasure ( data: IOListProductModelOptionalPriceGroupMeasureRequest ): Promise<IOListProductModelOptionalPriceGroupMeasureResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.listProductModelOptionalPriceGroupMeasure, data, false, true )
}
export async function be_getProductModelMeasurePrice ( data: IOGetProductModelMeasurePriceRequest ): Promise<IOGetProductModelMeasurePriceResponse> {
  return await backEndCallExtended( ApiPriceFunctionName.getProductModelMeasurePrice, data, false, true )
}